import React from 'react';
import { SpriteIcon } from '@/components/ui/SvgSprite';
import { toPersianDigits } from '@/utils/persianUtils';

/**
 * Exchange score in the price-page exchange table (same idea as TetherChand's `RatingBadge`): every chip has the
 * same size, the tier sets the colour (≥4.5 solid amber, ≥4 amber tint, lower neutral) and the bar along the
 * bottom edge stretches 2.5 → 5 over the chip width, so close scores (4 / 4.5 / 4.8) still read apart.
 * Display only: it does not change the row order.
 */
const RatingBadge: React.FC<{ rating: number; name?: string }> = ({ rating, name }) => {
    const score = Math.max(0, Math.min(5, rating || 0));
    if (score <= 0) return null;

    const tier = score >= 4.5 ? 'top' : score >= 4 ? 'good' : 'low';
    const fill = Math.max(0.06, Math.min(1, (score - 2.5) / 2.5));
    const label = toPersianDigits(
        score.toFixed(score * 10 === Math.round(score * 10) ? 1 : 2),
    ).replace('.', '٫');

    const chip =
        tier === 'top'
            ? 'bg-amber-400 text-amber-950 dark:bg-amber-400/90'
            : tier === 'good'
              ? 'bg-amber-500/15 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'
              : 'bg-zinc-100 text-zinc-500 dark:bg-white/[0.06] dark:text-zinc-400';
    const bar =
        tier === 'top' ? 'bg-amber-700/60' : tier === 'good' ? 'bg-amber-400' : 'bg-zinc-400/50';
    const title = `امتیاز ${label} از ۵${name ? ` در صفحهٔ معرفی ${name}` : ''}`;

    return (
        <span className='mt-0.5 inline-flex items-center gap-1' title={title}>
            <span
                className={`relative inline-flex h-[20px] w-[46px] items-center justify-center gap-0.5 overflow-hidden rounded-md text-[11px] font-bold leading-none ${chip}`}
            >
                <SpriteIcon
                    name='star'
                    size={10}
                    strokeWidth={tier === 'low' ? 2 : 1.5}
                    className={`shrink-0 ${tier === 'low' ? '' : 'fill-current'}`}
                />
                <span className='pt-px'>{label}</span>
                <span
                    aria-hidden='true'
                    className={`absolute bottom-0 right-0 h-[2px] ${bar}`}
                    style={{ width: `${fill * 100}%` }}
                />
            </span>
            <span className='max-sm:hidden text-[10px] font-medium text-gray-500 dark:text-gray-400'>
                از ۵
            </span>
        </span>
    );
};

export default RatingBadge;
